import { useState } from "react";
import { Mail, ArrowRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { newsletterSubscribe } from "@/lib/api";

const LANG_OPTIONS = [
    { code: "en", name: "English" },
    { code: "es", name: "Español" },
    { code: "zh", name: "中文" },
    { code: "hi", name: "हिन्दी" },
    { code: "ar", name: "العربية" },
    { code: "fr", name: "Français" },
    { code: "pt", name: "Português" },
];

const INTEREST_OPTIONS = [
    { key: "know_your_rights", label: "KNOW YOUR RIGHTS" },
    { key: "in_the_moment", label: "IN THE MOMENT" },
    { key: "fight_back", label: "FIGHT BACK" },
    { key: "diy_pro_se", label: "DIY PRO SE" },
];

export default function NewsletterSignup({
    source = "manual",
    accent = "#FF3B30",
    compact = false,
    headline = "Weekly Brief — In Your Inbox.",
    subheadline = "Questions worth asking, statutes cited, free resources, in your language. Every Sunday. No spam. Unsubscribe anytime.",
}) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [state, setStateAbbrev] = useState("");
    const [lang, setLang] = useState("en");
    const [interests, setInterests] = useState([
        "know_your_rights",
        "fight_back",
    ]);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const toggleInterest = (k) =>
        setInterests((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

    const submit = async (e) => {
        e.preventDefault();
        if (!email.includes("@")) {
            toast.error("Add a valid email.");
            return;
        }
        setSubmitting(true);
        try {
            await newsletterSubscribe({
                email: email.trim().toLowerCase(),
                name: name.trim(),
                state: state.trim(),
                language: lang,
                interests,
                source,
            });
            setDone(true);
            toast.success("You're in. First brief drops Sunday.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't subscribe.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div
                className="border-2 p-6 bg-white/5"
                style={{ borderColor: accent }}
                data-testid="newsletter-success"
            >
                <div
                    className="font-mono text-[10px] uppercase tracking-[0.3em] flex items-center gap-2"
                    style={{ color: accent }}
                >
                    <Check size={12} /> CONFIRMED
                </div>
                <div className="font-anton text-2xl uppercase mt-2">
                    The Brief is yours. Sundays at 8am, your time.
                </div>
                <p className="font-sans text-sm text-white/70 mt-2">
                    Check your inbox for the first one. Forward it to one person who needs it —
                    that's how the system gets smaller.
                </p>
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            className={`border-2 border-white/30 p-6 ${compact ? "" : "sm:p-8"} bg-[#0a0a0a]`}
            data-testid="newsletter-signup"
            style={{ borderColor: accent }}
        >
            <div
                className="font-mono text-[10px] uppercase tracking-[0.3em] mb-2 flex items-center gap-2"
                style={{ color: accent }}
            >
                <Mail size={11} /> HPM WEEKLY BRIEF · FREE · MULTILINGUAL
            </div>
            <h3 className={`font-anton uppercase leading-[0.95] ${compact ? "text-3xl" : "text-4xl sm:text-5xl"}`}>
                {headline}
            </h3>
            <p className="font-sans text-sm text-white/70 mt-3 max-w-xl">{subheadline}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                <input
                    data-testid="newsletter-email"
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm text-white placeholder-white/40 focus:border-white outline-none"
                    required
                />
                <input
                    data-testid="newsletter-name"
                    placeholder="First name (optional)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm text-white placeholder-white/40 focus:border-white outline-none"
                />
                <input
                    data-testid="newsletter-state"
                    placeholder="State (for local resources)"
                    value={state}
                    onChange={(e) => setStateAbbrev(e.target.value)}
                    className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm text-white placeholder-white/40 focus:border-white outline-none"
                />
                <select
                    data-testid="newsletter-lang"
                    value={lang}
                    onChange={(e) => setLang(e.target.value)}
                    className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm text-white focus:border-white outline-none"
                >
                    {LANG_OPTIONS.map((l) => (
                        <option key={l.code} value={l.code} className="bg-black">
                            {l.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="mt-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                    INTERESTS
                </div>
                <div className="flex flex-wrap gap-2">
                    {INTEREST_OPTIONS.map((o) => {
                        const active = interests.includes(o.key);
                        return (
                            <button
                                key={o.key}
                                type="button"
                                onClick={() => toggleInterest(o.key)}
                                data-testid={`newsletter-interest-${o.key}`}
                                className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 border-2 transition-colors ${
                                    active
                                        ? "bg-white text-black border-white"
                                        : "border-white/20 text-white/70 hover:border-white"
                                }`}
                            >
                                {o.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <button
                type="submit"
                disabled={submitting}
                data-testid="newsletter-submit"
                className="btn-brutal btn-brutal-red mt-5 w-full justify-center disabled:opacity-50"
            >
                {submitting ? (
                    <>
                        <Loader2 size={14} className="animate-spin" /> SUBSCRIBING...
                    </>
                ) : (
                    <>
                        SEND ME THE WEEKLY BRIEF <ArrowRight size={14} />
                    </>
                )}
            </button>
        </form>
    );
}
